/**
 * PC 左栏宽度：拖拽调整 + localStorage 记住（纯 UI 偏好，不进 IndexedDB）。
 * 拖拽用 Pointer Events + setPointerCapture（鼠标/触屏统一），move 期间只更新
 * state，松手才写 localStorage。
 */
import { useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';

const STORAGE_KEY = 'lhim.leftPaneWidth';
export const LEFT_PANE_DEFAULT = 320;
export const LEFT_PANE_MIN = 240;
/** 左栏最多占视口一半，右栏聊天区始终保有空间。 */
export const LEFT_PANE_MAX_RATIO = 0.5;

export function clampPaneWidth(px: number, viewportWidth: number): number {
  const max = Math.max(LEFT_PANE_MIN, Math.floor(viewportWidth * LEFT_PANE_MAX_RATIO));
  return Math.min(Math.max(Math.round(px), LEFT_PANE_MIN), max);
}

/** localStorage 原始值 → 宽度；非法/缺失返回 undefined（用默认值）。 */
export function parseStoredPaneWidth(raw: string | null): number | undefined {
  if (raw === null) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function readStored(): number | undefined {
  try {
    return parseStoredPaneWidth(localStorage.getItem(STORAGE_KEY));
  } catch {
    return undefined; // 隐私模式等拿不到 localStorage：用默认宽度即可
  }
}

export interface LeftPaneWidth {
  width: number;
  onDividerPointerDown: (e: JSX.TargetedPointerEvent<HTMLDivElement>) => void;
}

export function useLeftPaneWidth(): LeftPaneWidth {
  const [width, setWidth] = useState(
    () => clampPaneWidth(readStored() ?? LEFT_PANE_DEFAULT, window.innerWidth),
  );
  const widthRef = useRef(width);

  const onDividerPointerDown = (e: JSX.TargetedPointerEvent<HTMLDivElement>): void => {
    const divider = e.currentTarget;
    const startX = e.clientX;
    const startWidth = widthRef.current;
    divider.setPointerCapture(e.pointerId);

    const onMove = (ev: PointerEvent): void => {
      const next = clampPaneWidth(startWidth + ev.clientX - startX, window.innerWidth);
      widthRef.current = next;
      setWidth(next);
    };
    const onUp = (): void => {
      divider.removeEventListener('pointermove', onMove);
      try {
        localStorage.setItem(STORAGE_KEY, String(widthRef.current));
      } catch {
        // 存不了就下次用默认值，不影响本次使用
      }
    };
    divider.addEventListener('pointermove', onMove);
    divider.addEventListener('pointerup', onUp, { once: true });
    divider.addEventListener('pointercancel', onUp, { once: true });
  };

  return { width, onDividerPointerDown };
}
